import { useEffect, useState } from "react";
import axios, { getCsrfToken } from "../../api/axios";
import SidebarClient from "../../components/SidebarClient";
import QRCode from "react-qr-code";

export default function ClientSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    bulk_delay_ms: 800,
    daily_message_limit: -1,
    default_footer: "",
    allow_media: true,
  });

  const [profile, setProfile] = useState({ name: "", email: "", phone: "" });
  const [pw, setPw] = useState({ old_password: "", new_password: "" });

  // 2FA (Google Authenticator)
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [twoFaSetup, setTwoFaSetup] = useState(null); // { secret, otpauthUrl }
  const [twoFaCode, setTwoFaCode] = useState("");
  const [twoFaDisableCode, setTwoFaDisableCode] = useState("");
  const [twoFaBusy, setTwoFaBusy] = useState(false);

  // API IP Whitelist
  const [ipInfo, setIpInfo] = useState({ ip: "", items: [] });
  const [newIp, setNewIp] = useState("");
  const [newIpNote, setNewIpNote] = useState("");
  const [ipBusy, setIpBusy] = useState(false);

  // 2FA modal for IP whitelist changes
  const [showIp2fa, setShowIp2fa] = useState(false);
  const [ipTotp, setIpTotp] = useState("");
  const [ipTotpError, setIpTotpError] = useState("");
  const [ipPendingAction, setIpPendingAction] = useState(null); // { type: 'add' } or { type: 'remove', id }

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [sRes, pRes] = await Promise.all([
        axios.get("/client-settings"),
        axios.get("/client/profile"),
      ]);
      setSettings(sRes.data);
      setProfile({
        name: pRes.data?.name || "",
        email: pRes.data?.email || "",
        phone: pRes.data?.phone || "",
      });
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data.slice(0, 120) : null) ||
        err?.message;
      alert(msg || `Failed to load settings${status ? ` (HTTP ${status})` : ""}`);
    }
    setLoading(false);
  };

  const fetchTwoFaStatus = async () => {
    try {
      const res = await axios.get("/auth/2fa/status");
      setTwoFaEnabled(Boolean(res.data?.enabled));
    } catch (err) {
      // ignore; auth interceptor will handle invalid sessions
    }
  };

  const fetchIpWhitelist = async () => {
    try {
      const res = await axios.get("/whatsapp/ip-whitelist");
      setIpInfo({ ip: res.data?.ip || "", items: res.data?.items || [] });
      if (!newIp && res.data?.ip) setNewIp(res.data.ip);
    } catch (err) {
      // ignore
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchTwoFaStatus();
    fetchIpWhitelist();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startTwoFaSetup = async () => {
    setTwoFaBusy(true);
    try {
      const res = await axios.post("/auth/2fa/setup");
      setTwoFaSetup({
        secret: res.data?.secret,
        otpauthUrl: res.data?.otpauthUrl,
      });
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to start 2FA setup");
    }
    setTwoFaBusy(false);
  };

  const enableTwoFa = async () => {
    if (!twoFaCode) {
      alert("2FA code is required");
      return;
    }

    setTwoFaBusy(true);
    try {
      await axios.post("/auth/2fa/enable", { code: twoFaCode });
      alert("2FA enabled");
      setTwoFaEnabled(true);
      setTwoFaSetup(null);
      setTwoFaCode("");
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to enable 2FA");
    }
    setTwoFaBusy(false);
  };

  const disableTwoFa = async () => {
    if (!twoFaDisableCode) {
      alert("2FA code is required");
      return;
    }

    setTwoFaBusy(true);
    try {
      const csrfToken = await getCsrfToken(true);
      if (!csrfToken) {
        alert("Security token missing. Please refresh and try again.");
        return;
      }

      await axios.post(
        "/auth/2fa/disable",
        { code: twoFaDisableCode },
        {
          headers: {
            "x-csrf-token": csrfToken,
            "x-csrf-stored": csrfToken,
          },
        }
      );
      alert("2FA disabled");
      setTwoFaEnabled(false);
      setTwoFaSetup(null);
      setTwoFaDisableCode("");
    } catch (err) {
      alert(err?.response?.data?.message || "Failed to disable 2FA");
    }
    setTwoFaBusy(false);
  };

  const addIp = async () => {
    if (!newIp) {
      alert("IP is required");
      return;
    }

    setIpBusy(true);
    try {
      await axios.post("/whatsapp/ip-whitelist", { ip: newIp, note: newIpNote });
      setNewIpNote("");
      await fetchIpWhitelist();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      if (status === 401 && data?.requires2fa) {
        setIpTotp("");
        setIpTotpError("");
        setIpPendingAction({ type: "add" });
        setShowIp2fa(true);
      } else {
        alert(data?.message || "Failed to add IP");
      }
    }
    setIpBusy(false);
  };

  const removeIp = async (id) => {
    if (!window.confirm("Remove this IP from whitelist?")) return;

    setIpBusy(true);
    try {
      await axios.delete(`/whatsapp/ip-whitelist/${id}`);
      await fetchIpWhitelist();
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      if (status === 401 && data?.requires2fa) {
        setIpTotp("");
        setIpTotpError("");
        setIpPendingAction({ type: "remove", id });
        setShowIp2fa(true);
      } else {
        alert(data?.message || "Failed to remove IP");
      }
    }
    setIpBusy(false);
  };

  const submitIpTotp = async () => {
    if (!ipTotp) {
      setIpTotpError("Google Authenticator code is required");
      return;
    }

    if (!ipPendingAction) {
      setShowIp2fa(false);
      return;
    }

    setIpBusy(true);
    setIpTotpError("");

    try {
      if (ipPendingAction.type === "add") {
        await axios.post("/whatsapp/ip-whitelist", {
          ip: newIp,
          note: newIpNote,
          totp: ipTotp,
        });
        setNewIpNote("");
      } else if (ipPendingAction.type === "remove") {
        await axios.delete(`/whatsapp/ip-whitelist/${ipPendingAction.id}`, {
          headers: { "x-totp": ipTotp },
        });
      }

      await fetchIpWhitelist();
      setShowIp2fa(false);
      setIpTotp("");
      setIpPendingAction(null);
    } catch (err) {
      setIpTotpError(err?.response?.data?.message || "Invalid 2FA code");
    }

    setIpBusy(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put("/client-settings", settings);
      alert("Settings saved");
    } catch (err) {
      alert(err?.response?.data?.error || err?.response?.data?.message || "Failed to save settings");
    }
    setSaving(false);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      await axios.put("/client/profile", profile);
      alert("Profile updated");
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data.slice(0, 120) : null) ||
        err?.message;
      alert(msg || `Failed to update profile${status ? ` (HTTP ${status})` : ""}`);
    }
    setSaving(false);
  };

  const changePassword = async () => {
    if (!pw.old_password || !pw.new_password) {
      alert("Old and new password required");
      return;
    }
    setSaving(true);
    try {
      await axios.post("/client/change-password", pw);
      alert("Password changed");
      setPw({ old_password: "", new_password: "" });
    } catch (err) {
      const status = err?.response?.status;
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (typeof err?.response?.data === "string" ? err.response.data.slice(0, 120) : null) ||
        err?.message;
      alert(msg || `Failed to change password${status ? ` (HTTP ${status})` : ""}`);
    }
    setSaving(false);
  };

  return (
    <div className="flex flex-col lg:flex-row h-screen bg-gray-50">
      <SidebarClient />
      <div className="flex-1 p-4 md:p-6 lg:p-8 overflow-y-auto mt-14 lg:mt-0">
        {showIp2fa && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white p-8 rounded-3xl w-full max-w-md shadow-2xl">
              <h3 className="text-2xl font-bold mb-2 text-gray-800">2FA Required</h3>
              <p className="text-gray-500 mb-6">
                Enter the 6-digit code from Google Authenticator to change the IP whitelist.
              </p>

              <div className="space-y-4">
                <input
                  className="w-full border p-3 rounded-xl"
                  placeholder="6-digit code"
                  value={ipTotp}
                  onChange={(e) => setIpTotp(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") submitIpTotp();
                  }}
                />

                {ipTotpError && (
                  <p className="text-sm text-red-600 font-bold">{ipTotpError}</p>
                )}

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setShowIp2fa(false);
                      setIpTotp("");
                      setIpTotpError("");
                      setIpPendingAction(null);
                    }}
                    disabled={ipBusy}
                    className="flex-1 bg-gray-100 py-3 rounded-xl font-bold text-gray-700 disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={submitIpTotp}
                    disabled={ipBusy}
                    className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-bold disabled:opacity-50"
                  >
                    {ipBusy ? "Verifying..." : "Verify"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-900">Settings</h1>
            <p className="text-gray-500 mt-1">Customize how bulk and messaging behaves.</p>
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="bg-blue-600 text-white px-6 py-2 rounded-xl font-bold hover:bg-blue-700 transition disabled:opacity-50"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="space-y-6 max-w-3xl">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Profile</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">NAME</label>
                  <input
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={profile.name}
                    onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">EMAIL</label>
                  <input
                    type="email"
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={profile.email}
                    onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">WHATSAPP NUMBER</label>
                  <input
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    placeholder="e.g. 9198xxxxxxx"
                    value={profile.phone}
                    onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
                  />
                  <p className="text-xs text-gray-400 mt-1 ml-1">Digits only (country code + number)</p>
                </div>
              </div>
              <button
                onClick={saveProfile}
                disabled={saving}
                className="mt-4 bg-gray-900 text-white px-5 py-2 rounded-xl font-bold hover:bg-gray-800 transition disabled:opacity-50"
              >
                Update Profile
              </button>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Change Password</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">OLD PASSWORD</label>
                  <input
                    type="password"
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={pw.old_password}
                    onChange={(e) => setPw({ ...pw, old_password: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">NEW PASSWORD</label>
                  <input
                    type="password"
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={pw.new_password}
                    onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                  />
                </div>
              </div>
              <button
                onClick={changePassword}
                disabled={saving}
                className="mt-4 bg-gray-900 text-white px-5 py-2 rounded-xl font-bold hover:bg-gray-800 transition disabled:opacity-50"
              >
                Change Password
              </button>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-2">Security (2FA)</h2>
              <p className="text-gray-500 text-sm mb-4">
                Protect your account with Google Authenticator. New device login will require a 6-digit code.
              </p>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="font-bold text-gray-800">Google Authenticator</p>
                  <p className="text-sm text-gray-500">
                    Status: {twoFaEnabled ? "Enabled" : "Disabled"}
                  </p>
                </div>

                {!twoFaEnabled ? (
                  <button
                    onClick={startTwoFaSetup}
                    disabled={twoFaBusy}
                    className="bg-blue-600 text-white px-5 py-2 rounded-xl font-bold hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    {twoFaBusy ? "Please wait..." : twoFaSetup ? "Re-generate QR" : "Setup 2FA"}
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <input
                      className="border p-2 rounded-xl"
                      placeholder="6-digit code"
                      value={twoFaDisableCode}
                      onChange={(e) => setTwoFaDisableCode(e.target.value)}
                    />
                    <button
                      onClick={disableTwoFa}
                      disabled={twoFaBusy}
                      className="bg-red-600 text-white px-4 py-2 rounded-xl font-bold hover:bg-red-700 transition disabled:opacity-50"
                    >
                      Disable
                    </button>
                  </div>
                )}
              </div>

              {!twoFaEnabled && twoFaSetup?.otpauthUrl && (
                <div className="mt-6 space-y-4">
                  <div className="bg-white p-4 rounded-xl border border-gray-200 inline-block">
                    <QRCode value={twoFaSetup.otpauthUrl} size={180} />
                  </div>

                  <div>
                    <p className="text-xs font-bold text-gray-500">MANUAL SECRET</p>
                    <div className="bg-gray-900 text-green-400 p-3 rounded-xl font-mono text-xs break-all">
                      {twoFaSetup.secret}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <input
                      className="flex-1 border p-3 rounded-xl"
                      placeholder="Enter 6-digit code"
                      value={twoFaCode}
                      onChange={(e) => setTwoFaCode(e.target.value)}
                    />
                    <button
                      onClick={enableTwoFa}
                      disabled={twoFaBusy}
                      className="bg-green-600 text-white px-5 py-2 rounded-xl font-bold hover:bg-green-700 transition disabled:opacity-50"
                    >
                      Enable
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-2">API Security (IP Whitelist)</h2>
              <p className="text-gray-500 text-sm mb-4">
                Only whitelisted IPs can use your API key to send messages. If the list is empty, API requests will be blocked.
              </p>

              <div className="p-4 bg-gray-50 rounded-xl">
                <p className="text-sm text-gray-700">
                  Your current detected IP: <span className="font-mono font-bold">{ipInfo.ip || "(unknown)"}</span>
                </p>

                <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input
                    className="border p-3 rounded-xl"
                    placeholder="IP address"
                    value={newIp}
                    onChange={(e) => setNewIp(e.target.value)}
                  />
                  <input
                    className="border p-3 rounded-xl"
                    placeholder="Note (optional)"
                    value={newIpNote}
                    onChange={(e) => setNewIpNote(e.target.value)}
                  />
                  <button
                    onClick={addIp}
                    disabled={ipBusy}
                    className="bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition disabled:opacity-50"
                  >
                    {ipBusy ? "Please wait..." : "Add IP"}
                  </button>
                </div>

                <div className="mt-4">
                  {ipInfo.items?.length ? (
                    <div className="space-y-2">
                      {ipInfo.items.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between bg-white border border-gray-200 rounded-xl p-3"
                        >
                          <div>
                            <div className="font-mono font-bold text-gray-800">{item.ip}</div>
                            {item.note && <div className="text-xs text-gray-500">{item.note}</div>}
                          </div>
                          <button
                            onClick={() => removeIp(item.id)}
                            disabled={ipBusy}
                            className="text-red-600 font-bold hover:underline disabled:opacity-50"
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No IPs added yet.</p>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Bulk</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">DEFAULT BULK DELAY (ms)</label>
                  <input
                    type="number"
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={settings.bulk_delay_ms}
                    onChange={(e) => setSettings({ ...settings, bulk_delay_ms: Number(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">DAILY MESSAGE LIMIT</label>
                  <input
                    type="number"
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    value={settings.daily_message_limit}
                    onChange={(e) => setSettings({ ...settings, daily_message_limit: Number(e.target.value) })}
                  />
                  <p className="text-xs text-gray-400 mt-1 ml-1">Use -1 for unlimited</p>
                </div>
              </div>
            </div>

            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
              <h2 className="text-lg font-bold text-gray-800 mb-4">Message</h2>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-gray-500 ml-1">DEFAULT FOOTER</label>
                  <textarea
                    className="w-full border p-3 rounded-xl mt-1 focus:ring-2 focus:ring-blue-500 outline-none"
                    rows={3}
                    placeholder="Appended to every message (optional)"
                    value={settings.default_footer}
                    onChange={(e) => setSettings({ ...settings, default_footer: e.target.value })}
                  />
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                  <div>
                    <p className="font-bold text-gray-800">Allow Media</p>
                    <p className="text-sm text-gray-500">Allow sending photo/PDF</p>
                  </div>
                  <button
                    onClick={() => setSettings({ ...settings, allow_media: !settings.allow_media })}
                    className={`w-14 h-8 rounded-full transition-colors ${settings.allow_media ? "bg-green-500" : "bg-gray-300"}`}
                  >
                    <div className={`w-6 h-6 bg-white rounded-full shadow transition-transform ${settings.allow_media ? "translate-x-7" : "translate-x-1"}`} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
